import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  RadioButtonGroup,
  RadioButton,
  InlineNotification,
} from '@carbon/react';
import { ShapeExcept } from '@carbon/react/icons';
import styles from './toggleable-menu.module.scss';
import tableActionStyles from 'components/table/table-actions.module.scss';
import useClickOutside from 'lib/hooks/useClickOutside';
import classNames from 'classnames';

interface ThresholdFilterMenuProps {
  currentThreshold: number;
  onApply: (threshold: number) => void;
  onReset: () => void;
}

const getThresholdDescription = (threshold: number): string => {
  switch (threshold) {
    case 0:
      return 'No overlapping threshold applied. Assets will not be filtered based on overlap.';
    case 1:
      return 'More assets will be recognized as overlapping, including those with very minor overlaps.';
    case 2:
      return 'A moderate number of assets will be recognized as overlapping, filtering out only the smallest overlaps.';
    case 3:
      return 'The default threshold. Assets with more than 3% overlap are classified as overlapping.';
    case 4:
      return 'Fewer assets will be recognized as overlapping. Slight overlaps below 4% will be ignored.';
    case 5:
      return 'Only assets with significant overlap (greater than 5%) will be recognized. Minor overlaps will be filtered out.';
    default:
      return '';
  }
};

export const ThresholdFilterMenu: React.FC<ThresholdFilterMenuProps> = ({
  currentThreshold,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempThreshold, setTempThreshold] = useState<number>(currentThreshold);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Reset the selected threshold when the panel opens
  useEffect(() => {
    setTempThreshold(currentThreshold);
  }, [currentThreshold, isOpen]);

  // Use the custom hook for click outside detection
  useClickOutside({
    refs: [menuRef, buttonRef],
    onClickOutside: () => setIsOpen(false),
  });

  const handleApply = () => {
    onApply(tempThreshold);
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempThreshold(3); // Reset to default 3%
    onReset();
    setIsOpen(false);
  };

  return (
    <div className={styles.menuContainer}>
      <Button
        ref={buttonRef}
        renderIcon={ShapeExcept}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        kind="ghost"
        iconDescription="Overlap threshold filter"
        className={classNames(tableActionStyles.actionButton, 'overlap-button')}
      >
        {currentThreshold === 0 ? 'No Overlap' : `${currentThreshold}% Overlap`}
      </Button>
      {isOpen && (
        <div ref={menuRef} className={styles.menuPanel}>
          <div className={styles.menuContent}>
            <h4 className={styles.menuTitle}>Overlapping Threshold Filter</h4>
            <p className={styles.menuDescription}>
              The Overlapping Threshold determines which assets are recognized
              based on their overlap. Assets with more than 3% overlap are
              classified as overlapping, while those below this threshold are
              ignored. The default threshold is 3%.
            </p>
            <p className={styles.menuDescription}>
              Select a threshold to determine which assets are recognized as
              overlapping. Lower values include more assets with minor overlaps,
              while higher values filter them out.
            </p>
            <RadioButtonGroup
              name="threshold"
              valueSelected={tempThreshold}
              onChange={(value: string | number | undefined) =>
                setTempThreshold(value as number)
              }
              orientation="horizontal"
              className={styles.radioGroup}
            >
              <RadioButton id="threshold-none" value={0} labelText="None" />
              <RadioButton id="threshold-1" value={1} labelText="1%" />
              <RadioButton id="threshold-2" value={2} labelText="2%" />
              <RadioButton id="threshold-3" value={3} labelText="3%" />
              <RadioButton id="threshold-4" value={4} labelText="4%" />
              <RadioButton id="threshold-5" value={5} labelText="5%" />
            </RadioButtonGroup>

            <div className={styles.notificationContainer}>
              <InlineNotification
                kind="info"
                title=""
                subtitle={getThresholdDescription(tempThreshold)}
                hideCloseButton
              />
            </div>
          </div>
          <div
            className={classNames(
              styles.buttonContainer,
              'filter-panel-buttons'
            )}
          >
            <Button
              kind="secondary"
              size="xl"
              onClick={handleReset}
              className="filter-panel-action-btn"
            >
              Reset Filters
            </Button>
            <Button
              kind="primary"
              size="xl"
              onClick={handleApply}
              className="filter-panel-action-btn"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
